import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ onBack, userToken, onKeysUpdated, onModeChange }) => {
  // Settings state - ONLY wallet config
  const [settings, setSettings] = useState({
    hyperliquid_private_key: '',
    hyperliquid_account_address: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [addressVisible, setAddressVisible] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [savedAddress, setSavedAddress] = useState('');
  const [builderStatus, setBuilderStatus] = useState(null);
  const [builderLoading, setBuilderLoading] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
    checkConnectionStatus();
    checkBuilderStatus();
  }, []);

  const checkBuilderStatus = async () => {
    try {
      setBuilderLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/grid-bot/builder-approval-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBuilderStatus(res.data || null);
    } catch (err) {
      // 401 = no token; silent
      if (err?.response?.status !== 401) {
        console.warn('builder-approval-status failed:', err?.message || err);
      }
    } finally {
      setBuilderLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${API}/user/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          const addr = response.data.hyperliquid_account_address || '';
          setSavedAddress(addr);
          setSettings(prev => ({
            ...prev,
            hyperliquid_account_address: addr
          }));
        }
      } catch (err) {
        console.warn('Could not load settings:', err);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hyperliquid/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setIsConnected(response.data?.status === 'connected');
    } catch (err) {
      console.warn('Could not check Hyperliquid status:', err);
    }
  };

  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setError(null);
    setSuccess('');
  };

  const saveConfiguration = async () => {
    if (!settings.hyperliquid_account_address || !settings.hyperliquid_private_key) {
      setError('Please fill in both Main Wallet Address and API Wallet Private Key');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/hyperliquid/configure`, {
        private_key: settings.hyperliquid_private_key,
        account_address: settings.hyperliquid_account_address,
        position_size_pct: 0.05,
        max_leverage: 10,
        max_positions: 5
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        const balance = response.data.account_value;
        const warning = response.data.warning;
        const balanceMsg = balance > 0 ? ` Balance: $${balance.toFixed(2)}` : ' (Balance: $0)';
        setSuccess(`Wallet connected successfully!${balanceMsg}`);
        if (warning) {
          setError(warning);
        }
        setIsConnected(true);
        if (onKeysUpdated) onKeysUpdated();
      } else {
        setError(response.data.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError(error.response?.data?.error || 'Failed to connect wallet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-6 border-b border-gray-700">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="text-gray-400 text-sm">Connect your Hyperliquid wallet</p>
          </div>
          <button
            onClick={onBack}
            className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded text-sm transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-6">
        {/* Messages */}
        {error && (
          <div className="bg-red-900/30 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
            ⚠️ {error}
          </div>
        )}
        {success && (
          <div className="bg-green-900/30 border border-green-500 text-green-400 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        {/* Wallet Configuration Card */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center">
              <span className="text-green-400 mr-2">🔑</span>
              Hyperliquid Wallet
            </h2>
            {isConnected && (
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm">
                ✓ Connected
              </span>
            )}
          </div>

          <p className="text-gray-400 text-sm mb-6">
            Connect your Hyperliquid wallet for grid trading. You need your <strong className="text-white">main wallet address</strong> (where your USDC sits)
            and your <strong className="text-white">Hyperliquid API Wallet private key</strong> (generated on Hyperliquid's API page — trade-only, cannot withdraw).
          </p>

          <div className="bg-red-900/20 border border-red-500/40 rounded-lg p-3 mb-3">
            <p className="text-red-200 text-xs leading-relaxed">
              <strong>🛑 Never paste:</strong> your main wallet seed phrase, recovery phrase, or main-wallet private key. Actuvo only ever needs the
              {' '}<strong>API Wallet private key</strong> generated on Hyperliquid's API page. If you're unsure, stop and contact support.
            </p>
          </div>

          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-6">
            <p className="text-blue-300 text-xs leading-relaxed">
              <strong>How to find your Main Wallet Address:</strong> Go to{' '}
              <a href="https://app.hyperliquid.xyz/" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">app.hyperliquid.xyz</a>
              {' → Portfolio → copy the address from the top of the page or URL bar. '}
              <strong>This is NOT the same as your API Wallet address.</strong>
            </p>
          </div>

          <div className="space-y-4">
            {/* Main Wallet Address */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Main Wallet Address
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={
                    !addressVisible && savedAddress && settings.hyperliquid_account_address === savedAddress
                      ? `${savedAddress.slice(0, 8)}${'•'.repeat(26)}${savedAddress.slice(-6)}`
                      : settings.hyperliquid_account_address
                  }
                  onChange={(e) => handleSettingsChange('hyperliquid_account_address', e.target.value)}
                  onFocus={() => setAddressVisible(true)}
                  placeholder="0x..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 font-mono text-sm pr-16"
                />
                {savedAddress && (
                  <button
                    type="button"
                    onClick={() => setAddressVisible(!addressVisible)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-sm"
                  >
                    {addressVisible ? 'Hide' : 'Show'}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your Hyperliquid portfolio address (where your USDC balance is). This is NOT the API wallet address.
              </p>
            </div>

            {/* API Wallet Private Key */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hyperliquid API Wallet — Private Key
              </label>
              <div className="relative">
                <input
                  type={keyVisible ? "text" : "password"}
                  value={settings.hyperliquid_private_key}
                  onChange={(e) => handleSettingsChange('hyperliquid_private_key', e.target.value)}
                  placeholder="0x... (~66 chars) — API Wallet key from Hyperliquid's API page"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 font-mono text-sm pr-16"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  name={`hl-api-wallet-private-key-${Math.random().toString(36).slice(2, 8)}`}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  data-testid="hl-private-key-input"
                />
                <button
                  type="button"
                  onClick={() => setKeyVisible(!keyVisible)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-sm"
                  data-testid="hl-private-key-toggle-visibility"
                >
                  {keyVisible ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Generated on Hyperliquid → More → API → "Generate API Wallet". Trade-only, cannot withdraw. Revoke any time on Hyperliquid.
              </p>
            </div>

            {/* Save Button */}
            <button
              onClick={saveConfiguration}
              disabled={saving || !settings.hyperliquid_private_key || !settings.hyperliquid_account_address}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-semibold transition-colors"
            >
              {saving ? 'Connecting...' : isConnected ? 'Update Connection' : 'Connect Wallet'}
            </button>

            {/* Security Note */}
            <p className="text-xs text-gray-500 text-center mt-4">
              🔒 Your API Wallet key is encrypted at rest and never logged. API Wallets can only place/cancel orders — they cannot withdraw.
            </p>

            {/* Builder Codes — Hyperliquid on-chain fee approval */}
            {builderStatus?.enabled && (
              <div
                data-testid="settings-builder-codes-card"
                className={`mt-6 rounded-lg border p-4 ${
                  builderStatus.approved
                    ? 'bg-green-900/30 border-green-700'
                    : 'bg-amber-900/30 border-amber-700'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white">
                      Hyperliquid Builder Code
                    </span>
                    <span
                      data-testid="builder-status-badge"
                      className={`text-xs px-2 py-0.5 rounded ${
                        builderStatus.approved
                          ? 'bg-green-600 text-white'
                          : 'bg-amber-600 text-white'
                      }`}
                    >
                      {builderStatus.approved ? 'Approved' : 'Action Required'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={checkBuilderStatus}
                    disabled={builderLoading}
                    className="text-xs text-gray-400 hover:text-white disabled:opacity-50"
                    data-testid="builder-status-refresh"
                  >
                    {builderLoading ? '…' : 'Refresh'}
                  </button>
                </div>

                <p className="text-xs text-gray-300 mb-2">
                  actuvo.ai attaches a builder code (<strong>{builderStatus.fee_display}</strong>{' '}
                  per fill) to every order it places for you. This is{' '}
                  <strong>required by our Terms of Service</strong> and goes to
                  the actuvo.ai builder wallet on-chain. Half of Hyperliquid&rsquo;s
                  own maker fee.
                </p>

                {builderStatus.approved ? (
                  <p className="text-xs text-green-300">
                    ✓ On-chain approval found (max {builderStatus.on_chain_max / 10} bp).
                    Builder fee will be applied to all subsequent trades automatically.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-200">
                      You haven&rsquo;t approved the builder fee yet. Hyperliquid
                      requires this approval to be signed by your <strong>main wallet</strong>{' '}
                      (not the API wallet), so it cannot be done from inside actuvo.ai.
                      Approve it once in the Hyperliquid UI:
                    </p>
                    <ol className="text-xs text-amber-100 list-decimal list-inside space-y-1 pl-2">
                      <li>
                        Open{' '}
                        <a
                          href={builderStatus.approve_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-blue-300 hover:text-blue-200"
                          data-testid="builder-approve-link"
                        >
                          Hyperliquid &rarr; Builder Codes
                        </a>
                      </li>
                      <li>
                        Paste this builder address:{' '}
                        <code className="text-xs bg-black/40 px-1 rounded select-all" data-testid="builder-address-value">
                          {builderStatus.builder_address}
                        </code>
                      </li>
                      <li>
                        Set max fee to <strong>{builderStatus.fee_display}</strong> (or higher)
                        and sign the approval with your main wallet
                      </li>
                      <li>
                        Return here and click <strong>Refresh</strong>
                      </li>
                    </ol>
                    <p className="text-[11px] text-gray-400 mt-1">
                      You can revoke this approval at any time from the same
                      Hyperliquid page.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
